import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrandLogo } from './BrandLogo';
import { Link } from 'react-router-dom';
import { 
  Play, Pause, Volume2, VolumeX, 
  Maximize, Minimize, ExternalLink
} from 'lucide-react';

// Ambient music tracks
const MUSIC_TRACKS = {
  ambient1: {
    name: 'Ambient Dreams',
    url: 'https://cdn.pixabay.com/download/audio/2022/05/27/audio_1808fbf07a.mp3'
  },
  ambient2: {
    name: 'Soft Corporate',
    url: 'https://cdn.pixabay.com/download/audio/2022/03/15/audio_8cb749d484.mp3'
  },
  ambient3: {
    name: 'Gentle Piano',
    url: 'https://cdn.pixabay.com/download/audio/2021/11/25/audio_91b32e02f9.mp3'
  }
};

// Ken Burns presets - subtle movements for cinematic feel
const KEN_BURNS_PRESETS = [
  { scale: [1.0, 1.12], x: [0, -2], y: [0, -1.5] },   // Slow zoom in, drift up-left
  { scale: [1.12, 1.0], x: [-2, 1], y: [-1, 0.5] },   // Slow zoom out, drift down-right
  { scale: [1.0, 1.1], x: [0, 2], y: [0, -1] },       // Slow zoom in, drift up-right
  { scale: [1.1, 1.0], x: [1, -1], y: [-0.5, 0.5] },  // Slow zoom out, center drift
  { scale: [1.0, 1.15], x: [-1, 1], y: [0, -1] },     // Zoom in, pan right-up
  { scale: [1.08, 1.0], x: [0, 0], y: [-1.5, 1] },    // Zoom out, vertical pan
];

export default function PhotoGalleryPlayer({ 
  images = [], 
  settings = {}, 
  title = '',
  profileName = '',
  profileSlug = '',
  profileLogo = '',
  autoPlay = true,
  showControls = true,
  onClose = null
}) {
  // Track two layers for cross-dissolve
  const [layerA, setLayerA] = useState({ index: 0, opacity: 1, preset: 0, progress: 0 });
  const [layerB, setLayerB] = useState({ index: 1, opacity: 0, preset: 1, progress: 0 });
  const [activeLayer, setActiveLayer] = useState('A'); // Which layer is currently visible
  
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [totalProgress, setTotalProgress] = useState(0);
  
  const containerRef = useRef(null);
  const audioRef = useRef(null);
  const animationRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  
  const duration = (settings?.duration || 5) * 1000; // Time per image in ms
  const dissolveDuration = 2000; // 2 second cross-dissolve for smooth blend
  const musicTrack = settings?.music_track;
  
  // Preload all images
  useEffect(() => {
    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);
  
  // Get next Ken Burns preset (avoid repeating)
  const getNextPreset = useCallback((currentPreset) => {
    let next;
    do {
      next = Math.floor(Math.random() * KEN_BURNS_PRESETS.length);
    } while (next === currentPreset && KEN_BURNS_PRESETS.length > 1);
    return next;
  }, []);
  
  // Main animation loop - handles continuous Ken Burns and cross-dissolve
  useEffect(() => {
    if (!isPlaying || images.length <= 1) return;
    
    let lastTime = Date.now();
    const cycleTime = duration; // Time for one complete image cycle
    
    const animate = () => {
      const now = Date.now();
      const delta = now - lastTime;
      lastTime = now;
      
      // Update both layers' Ken Burns progress
      setLayerA(prev => {
        const newProgress = Math.min(prev.progress + (delta / cycleTime), 1);
        return { ...prev, progress: newProgress };
      });
      
      setLayerB(prev => {
        const newProgress = Math.min(prev.progress + (delta / cycleTime), 1);
        return { ...prev, progress: newProgress };
      });
      
      // Get current active layer info
      const currentLayer = activeLayer === 'A' ? layerA : layerB;
      const inactiveLayer = activeLayer === 'A' ? layerB : layerA;
      
      // Calculate where we are in the current image's display time
      const elapsedInCycle = currentLayer.progress * cycleTime;
      
      // Start cross-dissolve when we're near the end of current image
      const dissolveStart = cycleTime - dissolveDuration;
      
      if (elapsedInCycle >= dissolveStart) {
        // Calculate dissolve progress (0 to 1)
        const dissolveProgress = (elapsedInCycle - dissolveStart) / dissolveDuration;
        
        // Smooth easing for dissolve (ease-in-out)
        const easedProgress = dissolveProgress < 0.5 
          ? 2 * dissolveProgress * dissolveProgress 
          : 1 - Math.pow(-2 * dissolveProgress + 2, 2) / 2;
        
        // Update opacities for cross-dissolve
        if (activeLayer === 'A') {
          setLayerA(prev => ({ ...prev, opacity: 1 - easedProgress }));
          setLayerB(prev => ({ ...prev, opacity: easedProgress }));
        } else {
          setLayerB(prev => ({ ...prev, opacity: 1 - easedProgress }));
          setLayerA(prev => ({ ...prev, opacity: easedProgress }));
        }
      }
      
      // Complete transition when dissolve is done
      if (currentLayer.progress >= 1) {
        const nextIndex = (currentLayer.index + 2) % images.length;
        const nextPreset = getNextPreset(inactiveLayer.preset);
        
        if (activeLayer === 'A') {
          // A finished, B is now active, prepare A for next
          setLayerA({ index: nextIndex, opacity: 0, preset: nextPreset, progress: 0 });
          setActiveLayer('B');
        } else {
          // B finished, A is now active, prepare B for next
          setLayerB({ index: nextIndex, opacity: 0, preset: nextPreset, progress: 0 });
          setActiveLayer('A');
        }
      }
      
      // Update total progress for progress bar
      const activeIdx = activeLayer === 'A' ? layerA.index : layerB.index;
      const activeProgress = activeLayer === 'A' ? layerA.progress : layerB.progress;
      setTotalProgress(((activeIdx + activeProgress) / images.length) * 100);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, images.length, duration, activeLayer, layerA.index, layerA.progress, layerB.index, layerB.progress, getNextPreset]);
  
  // Audio handling
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
      if (isPlaying && !isMuted) {
        audioRef.current.play().catch(() => {});
      }
    }
  }, [isMuted, isPlaying]);
  
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
    if (audioRef.current) {
      if (!isPlaying) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  };
  
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };
  
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  
  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'm') {
        toggleMute();
      } else if (e.key === 'f') {
        toggleFullscreen();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, isMuted]);
  
  if (!images || images.length === 0) {
    return (
      <div className="aspect-video bg-zinc-900 rounded-xl flex items-center justify-center">
        <p className="text-zinc-500">No images available</p>
      </div>
    );
  }
  
  const musicUrl = musicTrack ? MUSIC_TRACKS[musicTrack]?.url : null;
  
  // Calculate Ken Burns transform for a layer
  const getTransform = (preset, progress) => {
    const p = KEN_BURNS_PRESETS[preset];
    if (!p) return 'scale(1) translate(0%, 0%)';
    
    // Smooth easing for Ken Burns (ease-in-out)
    const eased = progress < 0.5 
      ? 2 * progress * progress 
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    
    const scale = p.scale[0] + (p.scale[1] - p.scale[0]) * eased;
    const x = p.x[0] + (p.x[1] - p.x[0]) * eased;
    const y = p.y[0] + (p.y[1] - p.y[0]) * eased;
    
    return `scale(${scale}) translate(${x}%, ${y}%)`;
  };
  
  // Render an image layer with Ken Burns
  const renderLayer = (layer, zIndex) => {
    const transform = getTransform(layer.preset, layer.progress);
    const imgSrc = images[layer.index];
    
    return (
      <div 
        key={`layer-${zIndex}`}
        className="absolute inset-0 flex items-center justify-center"
        style={{ 
          zIndex,
          opacity: layer.opacity,
          transition: 'opacity 100ms linear', // Smooth micro-transitions
        }}
      >
        {/* Blurred background for this layer */}
        <div 
          className="absolute inset-[-20%] w-[140%] h-[140%]"
          style={{
            backgroundImage: `url(${imgSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(40px) brightness(0.4) saturate(1.2)',
            transform: transform,
          }}
        />
        
        {/* Main image with Ken Burns */}
        <img
          src={imgSrc}
          alt=""
          className="relative max-w-[90%] max-h-[85%] object-contain"
          style={{ 
            transform: transform,
            filter: 'drop-shadow(0 10px 40px rgba(0,0,0,0.7))',
            willChange: 'transform',
          }}
        />
      </div>
    );
  };
  
  const currentImageIndex = activeLayer === 'A' ? layerA.index : layerB.index;
  
  return (
    <div 
      ref={containerRef}
      className={`relative bg-black overflow-hidden ${
        isFullscreen 
          ? 'fixed inset-0 z-50' 
          : 'aspect-video rounded-xl border-2 border-zinc-700/80 shadow-2xl shadow-black/50 ring-1 ring-white/10'
      }`}
      onMouseEnter={() => setShowOverlay(true)}
      onMouseLeave={() => setShowOverlay(false)}
      data-testid="photo-gallery-player"
    >
      {/* Background music */}
      {musicUrl && (
        <audio ref={audioRef} loop>
          <source src={musicUrl} type="audio/mpeg" />
        </audio>
      )}
      
      {/* Base black background */}
      <div className="absolute inset-0 bg-black" />
      
      {/* Two image layers for cross-dissolve - render both, opacity controls visibility */}
      {renderLayer(layerA, 1)}
      {renderLayer(layerB, 2)}
      
      {/* Subtle vignette overlay for cinematic feel */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)'
        }}
      />
      
      {/* Top gradient for text readability */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
      
      {/* Bottom gradient for controls */}
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
      
      {/* Top bar with branding */}
      <div className={`absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10 transition-opacity duration-500 ${showOverlay ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center gap-3">
          <div className="px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-lg border border-white/10">
            <BrandLogo className="h-6" testId="gallery-logo" />
          </div>
          {title && (
            <div className="px-3 py-1.5 bg-black/30 backdrop-blur-md rounded-lg border border-white/5 hidden md:block">
              <span className="text-white/80 text-sm">{title}</span>
            </div>
          )}
        </div>
        
        {profileSlug && (
          <Link
            to={`/p/${profileSlug}`}
            className="px-4 py-2 bg-[#C41E3A] hover:bg-[#A01830] text-white text-sm font-medium rounded-lg flex items-center gap-2 transition-colors shadow-lg"
          >
            <span>View {profileName || 'Profile'}</span>
            <ExternalLink className="w-4 h-4" />
          </Link>
        )}
      </div>
      
      {/* Video-style bottom controls */}
      {showControls && (
        <div className={`absolute bottom-0 left-0 right-0 z-10 transition-opacity duration-500 ${showOverlay ? 'opacity-100' : 'opacity-0'}`}>
          {/* Progress bar */}
          <div className="px-4 mb-1">
            <div className="h-1 bg-white/20 rounded-full overflow-hidden cursor-pointer group">
              <div 
                className="h-full bg-gradient-to-r from-[#C41E3A] to-[#FF4D6A] rounded-full relative"
                style={{ width: `${totalProgress}%`, transition: 'width 100ms linear' }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </div>
          
          {/* Controls row */}
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all hover:scale-105"
                data-testid="gallery-play-pause-btn"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              
              {/* Mute/Unmute */}
              {musicTrack && (
                <button
                  onClick={toggleMute}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all hover:scale-105"
                  data-testid="gallery-mute-btn"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              )}
              
              {/* Counter */}
              <div className="ml-2 text-white/80 text-sm font-medium tabular-nums">
                <span className="text-white">{currentImageIndex + 1}</span>
                <span className="text-white/50 mx-1">/</span>
                <span className="text-white/50">{images.length}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Playing indicator */}
              {isPlaying && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full mr-2">
                  <div className="flex items-end gap-0.5 h-3">
                    <div className="w-0.5 bg-[#C41E3A] rounded-full animate-pulse" style={{ height: '50%', animationDuration: '0.8s' }} />
                    <div className="w-0.5 bg-[#C41E3A] rounded-full animate-pulse" style={{ height: '100%', animationDuration: '0.6s', animationDelay: '0.1s' }} />
                    <div className="w-0.5 bg-[#C41E3A] rounded-full animate-pulse" style={{ height: '70%', animationDuration: '0.7s', animationDelay: '0.2s' }} />
                    <div className="w-0.5 bg-[#C41E3A] rounded-full animate-pulse" style={{ height: '40%', animationDuration: '0.9s', animationDelay: '0.15s' }} />
                  </div>
                  <span className="text-white/70 text-xs font-medium">Playing</span>
                </div>
              )}
              
              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all hover:scale-105"
                data-testid="gallery-fullscreen-btn"
              >
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
